import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService } from '../utils/api';

interface Question {
  id: string;
  type: 'OpenQuestion' | 'SingleChoice' | 'TrueFalse';
  text: string;
  grade: number | null;
  options: string[] | null;
  correctAnswer: string | null;
  order: number;
}

interface AssignmentData {
  lessonName: string;
  titel: string;
  unitId: number;
  active: boolean;
  order: number;
  questions: Omit<Question, 'id'>[];
  totalGrade: number | null;
  dueDate: string;
}

interface AssignmentResponse {
  id: number;
  lessonId: number;
  lessonName: string;
  questions: Question[];
  totalGrade: number | null;
  createdAt: string;
  dueDate: string;
  isActive: boolean;
}

const Assignment = () => {
  const { courseId, unitId } = useParams<{ courseId: string; unitId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const lessonId = searchParams.get('lessonId');
  
  const [lessonName, setLessonName] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<Question>({
    id: '',
    type: 'OpenQuestion',
    text: '',
    grade: null,
    options: null,
    correctAnswer: null,
    order: 1
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [assignmentId, setAssignmentId] = useState<number | null>(null);

  // Fetch assignment data if lessonId is provided
  useEffect(() => {
    const fetchAssignment = async () => {
      if (!lessonId) return;
      
      setIsLoading(true);
      try {
        const response = await authFetch(apiUrl(`Assignment/by-lesson/${lessonId}`), {
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        const data = await response.json();
        
        if (data.success && data.data) {
          const assignment: AssignmentResponse = data.data;
          setAssignmentId(assignment.id);
          setLessonName(assignment.lessonName);
          
          // Map questions from API to component format
          const mappedQuestions: Question[] = assignment.questions.map(q => ({
            id: q.id,
            type: q.type,
            text: q.text,
            grade: q.grade,
            options: q.options,
            correctAnswer: q.correctAnswer,
            order: q.order
          }));
          setQuestions(mappedQuestions);
        }
      } catch (error) {
        console.error('Error fetching assignment:', error);
        setSaveMessage({
          type: 'error',
          text: 'فشل في تحميل بيانات الواجب'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssignment();
  }, [lessonId]);


  const handleQuestionTypeChange = (type: 'OpenQuestion' | 'SingleChoice' | 'TrueFalse') => {
    setCurrentQuestion({
      ...currentQuestion,
      type,
      options: type === 'TrueFalse' ? ['صح', 'خطأ'] : type === 'SingleChoice' ? ['', '', '', ''] : null,
      correctAnswer: null
    });
  };

  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setCurrentQuestion({
      id: '',
      type: 'OpenQuestion',
      text: '',
      grade: null,
      options: null,
      correctAnswer: null,
      order: questions.length + 1
    });
    setShowQuestionForm(true);
  };

  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    setCurrentQuestion({ ...question });
    setShowQuestionForm(true);
  };

  const handleSaveQuestion = () => {
    if (!currentQuestion.text.trim()) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى إدخال نص السؤال'
      });
      return;
    }

    if (currentQuestion.type === 'SingleChoice') {
      if (!currentQuestion.options || currentQuestion.options.some(opt => !opt.trim())) {
        setSaveMessage({
          type: 'error',
          text: 'يرجى ملء جميع الخيارات'
        });
        return;
      }
      if (!currentQuestion.correctAnswer) {
        setSaveMessage({
          type: 'error',
          text: 'يرجى اختيار الإجابة الصحيحة'
        });
        return;
      }
    }

    if (currentQuestion.type === 'TrueFalse') {
      if (!currentQuestion.correctAnswer) {
        setSaveMessage({
          type: 'error',
          text: 'يرجى اختيار الإجابة الصحيحة'
        });
        return;
      }
    }

    if (editingQuestion) {
      // Update existing question
      setQuestions(questions.map(q => 
        q.id === editingQuestion.id ? currentQuestion : q
      ));
    } else {
      // Add new question
      const newQuestion: Question = {
        ...currentQuestion,
        id: `temp-${Date.now()}`,
        order: questions.length + 1
      };
      setQuestions([...questions, newQuestion]);
    }

    setShowQuestionForm(false);
    setEditingQuestion(null);
    setSaveMessage(null);
  };

  const handleDeleteQuestion = (questionId: string) => {
    setQuestions(questions.filter(q => q.id !== questionId).map((q, index) => ({
      ...q,
      order: index + 1
    })));
  };

  const handleOptionChange = (index: number, value: string) => {
    if (!currentQuestion.options) return;
    const newOptions = [...currentQuestion.options];
    newOptions[index] = value;
    setCurrentQuestion({
      ...currentQuestion,
      options: newOptions
    });
  };

  const handleSaveAssignment = async () => {
    if (!lessonName.trim()) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى إدخال اسم الدرس'
      });
      return;
    }

    if (!unitId) {
      setSaveMessage({
        type: 'error',
        text: 'معرف المحاضرة غير موجود'
      });
      return;
    }

    if (questions.length === 0) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى إضافة سؤال واحد على الأقل'
      });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Calculate the order (last order + 1) - only for new assignments
      let order = 1;
      if (!assignmentId) {
        // For new assignments, we'll use order 1 or increment based on existing lessons
        // Since we're not fetching the course tree, we'll use a default order
        order = 1;
      }

      // Prepare questions for API (remove id field for new questions, keep for existing)
      const questionsForAPI: Omit<Question, 'id'>[] = questions.map(q => {
        if (q.type === 'SingleChoice' || q.type === 'TrueFalse') {
          return {
            type: q.type,
            text: q.text,
            grade: q.grade,
            order: q.order,
            options: q.options || [],
            correctAnswer: q.correctAnswer
          };
        }
        
        // OpenQuestion type
        return {
          type: q.type,
          text: q.text,
          grade: q.grade,
          order: q.order,
          options: null,
          correctAnswer: null
        };
      });

      const assignmentData: AssignmentData = {
        lessonName: lessonName.trim(),
        titel: lessonName.trim(),
        unitId: Number(unitId),
        active: true,
        order: order,
        questions: questionsForAPI,
        totalGrade: questions.reduce((sum, q) => sum + (q.grade || 0), 0) || null,
        dueDate: new Date().toISOString()
      };

      // If assignmentId exists, this is an update (but API might not support update yet)
      // For now, we'll still use create endpoint
      const response = await authFetch(apiUrl('Assignment/create'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify(assignmentData)
      });

      const data = await response.json();

      if (data.success) {
        setSaveMessage({
          type: 'success',
          text: assignmentId ? 'تم تحديث الواجب بنجاح' : 'تم إنشاء الواجب بنجاح'
        });

        // Navigate back to course detail after 2 seconds
        setTimeout(() => {
          navigate(`/courses/${courseId}`);
        }, 2000);
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || (assignmentId ? 'فشل في تحديث الواجب' : 'فشل في إنشاء الواجب')
        });
      }
    } catch (error) {
      console.error('Error creating assignment:', error);
      setSaveMessage({
        type: 'error',
        text: 'فشل في إنشاء الواجب. يرجى المحاولة مرة أخرى لاحقًا.'
      });
    } finally {
      setIsSaving(false);
      
      // Clear the message after 3 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Save/Error Message Toast */}
        {saveMessage && (
          <div className={`fixed top-4 left-4 px-6 py-3 rounded-lg shadow-lg z-50 flex items-center ${
            saveMessage.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {saveMessage.type === 'success' ? (
              <CheckIcon className="h-5 w-5 ml-2" />
            ) : (
              <XMarkIcon className="h-5 w-5 ml-2" />
            )}
            {saveMessage.text}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center space-x-4 mb-6">
          <button
            onClick={() => navigate(`/courses/${courseId}`)}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="w-5 h-5 ml-1" />
            العودة إلى الدورة
          </button>
          <div>
            <h1 className="text-3xl font-bold text-dark">
              {assignmentId ? 'تعديل واجب' : 'إنشاء واجب'}
            </h1>
          </div>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          /* Main Form */
          <div className="bg-white rounded-lg shadow p-6">
          <div className="space-y-6">
            {/* Lesson Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                اسم الدرس *
              </label>
              <input
                type="text"
                value={lessonName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLessonName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                placeholder="مثال: واجب الأسبوع الأول - أساسيات البرمجة"
                required
              />
            </div>

            {/* Questions Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">الأسئلة</h2>
                <button
                  onClick={handleAddQuestion}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <PlusIcon className="w-5 h-5" />
                  إضافة سؤال
                </button>
              </div>

              {questions.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">لم تتم إضافة أي أسئلة بعد. انقر على "إضافة سؤال" لإنشاء أول سؤال.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {questions.map((question, index) => (
                    <div key={question.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-medium">السؤال {index + 1}</h4>
                          <span className="text-xs text-gray-500">
                            {question.type === 'OpenQuestion' ? 'سؤال مفتوح' : 
                             question.type === 'SingleChoice' ? 'اختيار من متعدد' : 
                             'صح/خطأ'}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditQuestion(question)}
                            className="text-blue-600 hover:text-blue-700"
                            title="تعديل السؤال"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="text-red-600 hover:text-red-700"
                            title="حذف السؤال"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      <p className="mb-3">{question.text}</p>
                      {question.type === 'SingleChoice' && question.options && (
                        <div className="grid grid-cols-2 gap-2">
                          {question.options.map((option, optIndex) => (
                            <div
                              key={optIndex}
                              className={`p-2 border rounded ${
                                question.correctAnswer === option
                                  ? 'bg-green-50 border-green-500'
                                  : ''
                              }`}
                            >
                              {String.fromCharCode(65 + optIndex)}. {option}
                              {question.correctAnswer === option && (
                                <span className="ml-2 text-green-600">✓</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {question.type === 'TrueFalse' && question.options && (
                        <div className="grid grid-cols-2 gap-2">
                          {question.options.map((option) => (
                            <div
                              key={option}
                              className={`p-2 border rounded ${
                                question.correctAnswer === option
                                  ? 'bg-green-50 border-green-500'
                                  : ''
                              }`}
                            >
                              {option}
                              {question.correctAnswer === option && (
                                <span className="ml-2 text-green-600">✓</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {question.grade && (
                        <div className="mt-2 text-sm text-gray-600">
                          الدرجة: {question.grade}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-4 pt-4 border-t">
              <button
                onClick={() => navigate(`/courses/${courseId}`)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleSaveAssignment}
                disabled={isSaving}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center"
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -mr-1 ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري الحفظ...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4 ml-2" />
                    {assignmentId ? 'حفظ التغييرات' : 'حفظ الواجب'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Question Form Modal */}
      {showQuestionForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">
              {editingQuestion ? 'تعديل السؤال' : 'إضافة سؤال جديد'}
            </h2>
            <div className="space-y-4">
              {/* Question Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  نوع السؤال *
                </label>
                <select
                  value={currentQuestion.type}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleQuestionTypeChange(e.target.value as 'OpenQuestion' | 'SingleChoice' | 'TrueFalse')}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="OpenQuestion">سؤال مفتوح</option>
                  <option value="SingleChoice">اختيار من متعدد</option>
                  <option value="TrueFalse">صح/خطأ</option>
                </select>
              </div>

              {/* Question Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  نص السؤال *
                </label>
                <textarea
                  value={currentQuestion.text}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCurrentQuestion({ ...currentQuestion, text: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="أدخل نص السؤال..."
                  rows={3}
                  required
                />
              </div>

              {/* Grade */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الدرجة (اختياري)
                </label>
                <input
                  type="number"
                  value={currentQuestion.grade || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentQuestion({ 
                    ...currentQuestion, 
                    grade: e.target.value ? Number(e.target.value) : null 
                  })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="أدخل الدرجة"
                  min="0"
                />
              </div>

              {/* Options for SingleChoice */}
              {currentQuestion.type === 'SingleChoice' && currentQuestion.options && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    الخيارات * (اختر الإجابة الصحيحة)
                  </label>
                  {currentQuestion.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <input
                          type="radio"
                          name="correctOption"
                          checked={currentQuestion.correctAnswer === option}
                          onChange={() => setCurrentQuestion({ 
                            ...currentQuestion, 
                            correctAnswer: option 
                          })}
                          className="w-4 h-4 text-primary focus:ring-primary"
                        />
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center">
                          <span className="w-6 text-gray-700">{String.fromCharCode(65 + index)}.</span>
                          <input
                            type="text"
                            value={option}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOptionChange(index, e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg"
                            placeholder={`الخيار ${String.fromCharCode(65 + index)}`}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Options for TrueFalse */}
              {currentQuestion.type === 'TrueFalse' && currentQuestion.options && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    اختر الإجابة الصحيحة *
                  </label>
                  {currentQuestion.options.map((option) => (
                    <div key={option} className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <input
                          type="radio"
                          name="correctAnswer"
                          checked={currentQuestion.correctAnswer === option}
                          onChange={() => setCurrentQuestion({ 
                            ...currentQuestion, 
                            correctAnswer: option 
                          })}
                          className="w-4 h-4 text-primary focus:ring-primary"
                        />
                      </div>
                      <div className="flex-grow">
                        <div className="px-3 py-2">{option}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => {
                  setShowQuestionForm(false);
                  setEditingQuestion(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleSaveQuestion}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                {editingQuestion ? 'تحديث السؤال' : 'إضافة السؤال'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assignment;


